import React, { createContext, useContext, useEffect, useState } from 'react';
import { authApi, AuthUser, Provider } from '../api/auth';
import { ThemeId, ThemeState, mergeProgress, setBackendSync } from '../data/learning';

interface AuthCtx {
  user: AuthUser | null;
  providers: Provider[];
  loading: boolean;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthCtx>({ user: null, providers: [], loading: true, logout: async () => {} });
export const useAuth = () => useContext(Ctx);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      const [provs, u] = await Promise.all([authApi.providers(), authApi.me()]);
      if (!active) return;
      setProviders(provs);
      setUser(u);
      if (u) {
        // Server-voortgang ophalen, samenvoegen met lokale, en write-through inschakelen.
        const server = await authApi.getProgress();
        const merged = mergeProgress(server);
        setBackendSync((id: ThemeId, st: ThemeState) => authApi.putProgress(id, st));
        (Object.keys(merged) as ThemeId[]).forEach((id) => authApi.putProgress(id, merged[id]));
      }
      setLoading(false);
      // ?login=ok/error uit de URL opschonen
      if (/[?&]login=/.test(window.location.search)) {
        window.history.replaceState({}, '', window.location.pathname);
      }
    })();
    return () => { active = false; };
  }, []);

  const logout = async () => {
    await authApi.logout();
    setBackendSync(null);
    setUser(null);
  };

  return <Ctx.Provider value={{ user, providers, loading, logout }}>{children}</Ctx.Provider>;
};
